import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, UserPlus, ChevronLeft, ChevronRight, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { staffApi } from "@/lib/staffApi";
import { ErrorState } from "@/components/BookingShell";
import { SkeletonListItem } from "@/components/Skeletons";
import { useQuery, useDebouncedValue, invalidate } from "@/lib/queryCache";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function StaffCustomers() {
    const [params, setParams] = useSearchParams();
    const [newOpen, setNewOpen] = useState(params.get("new") === "1");
    const search = params.get("q") || "";
    const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
    const rawPageSize = parseInt(params.get("size") || "25", 10);
    const pageSize = PAGE_SIZE_OPTIONS.includes(rawPageSize) ? rawPageSize : 25;

    // Debounce keeps Amelia calm while typing.
    const debouncedSearch = useDebouncedValue(search, 300);

    const cacheKey = `staff:customers:q=${debouncedSearch}:p=${page}:s=${pageSize}`;
    const { data, error, isLoading, hasStaleData, isFetching, refetch } = useQuery(
        cacheKey,
        () => staffApi.listCustomers(debouncedSearch, pageSize, page),
        { staleMs: 30_000 },
    );

    const setParam = (k, v) => {
        const p = new URLSearchParams(params);
        if (v == null || v === "" || v === false) p.delete(k);
        else p.set(k, String(v));
        // Reset page on filter/size change
        if (k === "q" || k === "size") p.delete("page");
        setParams(p, { replace: true });
    };

    const customers = data?.customers || [];
    const total = data?.total ?? 0;
    const totalPages = data?.total_pages ?? 1;
    const hasMore = data?.has_more ?? false;
    const rangeStart = customers.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = (page - 1) * pageSize + customers.length;

    return (
        <div className="space-y-5" data-testid="staff-customers">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-serif font-light text-3xl md:text-4xl text-white">Customers</h1>
                    <p className="text-sm text-white/60 mt-1">Manage your customer records.</p>
                </div>
                <button onClick={() => setNewOpen(true)} data-testid="open-new-customer"
                        className="rounded-full bg-gold text-navy px-5 h-11 font-semibold text-sm inline-flex items-center gap-2">
                    <UserPlus className="h-4 w-4" /> New customer
                </button>
            </div>

            {/* Search — white pill matches Forms/Services */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                <input
                    data-testid="customer-search"
                    placeholder="Search all customers by name, email or phone"
                    value={search}
                    onChange={(e) => setParam("q", e.target.value)}
                    className={`w-full h-11 pl-10 rounded-full bg-white text-navy placeholder-slate-400 text-[12px] sm:text-sm border border-slate-200 focus:border-gold outline-none shadow-sm ${isFetching ? "pr-24" : "pr-3"}`}
                />
                {isFetching && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] text-slate-400"
                          data-testid="customer-searching">
                        Searching…
                    </span>
                )}
            </div>

            {error && !data && <ErrorState error={error} onRetry={refetch} />}

            {isLoading && !data && (
                <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-2" data-testid="customer-skeleton">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="border-b border-slate-100 last:border-0 px-3">
                            <SkeletonListItem />
                        </div>
                    ))}
                </div>
            )}

            {data && (
                <>
                    {/* Count + page-size selector */}
                    <div className="flex items-center justify-between gap-3 text-xs flex-wrap">
                        <span className="text-slate-600" data-testid="customer-count">
                            {total > 0
                                ? `Showing ${rangeStart}–${rangeEnd} of ${total} customer${total === 1 ? "" : "s"}`
                                : "No customers"}
                            {hasStaleData && (
                                <span className="ml-2 text-amber-600 uppercase tracking-[0.18em]" data-testid="customers-stale-indicator">
                                    {isFetching ? "Refreshing…" : "Cached"}
                                </span>
                            )}
                        </span>
                        <label className="inline-flex items-center gap-2 text-slate-500">
                            <span className="uppercase tracking-[0.16em] text-[10px]">Per page</span>
                            <select
                                data-testid="customers-page-size"
                                value={pageSize}
                                onChange={(e) => setParam("size", e.target.value)}
                                className="h-8 rounded-full bg-white text-navy border border-slate-200 text-xs px-3 focus:border-gold outline-none"
                            >
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {customers.length === 0 ? (
                        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 text-center text-slate-500"
                             data-testid="customer-empty">
                            No customers found.
                        </div>
                    ) : (
                        <ul className="space-y-2.5" data-testid="customer-list">
                            {customers.map((c) => (
                                <li key={c.id}>
                                    <CustomerRow customer={c} />
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Full pagination — Previous / Page X of Y / Next */}
                    {(page > 1 || hasMore) && (
                        <div className="flex items-center justify-between gap-3 pt-2" data-testid="customers-pagination">
                            <button
                                type="button"
                                onClick={() => setParam("page", Math.max(1, page - 1))}
                                disabled={page <= 1}
                                data-testid="customers-prev"
                                className="h-10 px-4 rounded-full bg-white text-navy border border-slate-200 shadow-sm text-xs font-medium inline-flex items-center gap-1 disabled:opacity-40 hover:border-gold"
                            >
                                <ChevronLeft className="h-3.5 w-3.5" /> Previous
                            </button>
                            <span className="text-xs text-slate-600 font-medium" data-testid="customers-page-label">
                                Page {page}{totalPages > 1 ? ` of ${totalPages}` : ""}
                            </span>
                            <button
                                type="button"
                                onClick={() => setParam("page", page + 1)}
                                disabled={!hasMore}
                                data-testid="customers-next"
                                className="h-10 px-4 rounded-full bg-white text-navy border border-slate-200 shadow-sm text-xs font-medium inline-flex items-center gap-1 disabled:opacity-40 hover:border-gold"
                            >
                                Next <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </>
            )}

            {newOpen && (
                <NewCustomerModal
                    onClose={() => setNewOpen(false)}
                    onCreated={() => {
                        setNewOpen(false);
                        invalidate("staff:customers");
                        invalidate("amelia:customers");
                        refetch();
                    }}
                />
            )}
        </div>
    );
}

function displayNameOf(c) {
    const first = (c.firstName || "").trim();
    const last = (c.lastName || "").trim();
    return (first || last) ? `${first} ${last}`.trim() : (c.email || "Unnamed");
}

function CustomerRow({ customer: c }) {
    const initials = ((c.firstName?.[0] || "") + (c.lastName?.[0] || "")).toUpperCase()
        || (c.email?.[0] || "?").toUpperCase();
    return (
        <Link
            to={`/staff/customers/${c.id}`}
            data-testid={`customer-row-${c.id}`}
            className="block rounded-xl bg-[#0E1746] surface-highlight shadow-lg hover:shadow-xl transition-shadow"
        >
            <div className="flex items-start gap-3 p-4">
                <span
                    aria-hidden
                    className="shrink-0 h-10 w-10 rounded-full bg-gold/20 text-gold font-semibold text-sm flex items-center justify-center"
                >
                    {initials}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold text-[15px] leading-snug break-words">
                        {displayNameOf(c)}
                    </div>
                    <div className="mt-1 text-[13px] text-white/70 space-y-0.5">
                        {c.email && (
                            <div className="flex items-center gap-1.5 truncate">
                                <Mail className="h-3.5 w-3.5 text-gold/70 shrink-0" />
                                <span className="truncate">{c.email}</span>
                            </div>
                        )}
                        {c.phone && (
                            <div className="flex items-center gap-1.5">
                                <Phone className="h-3.5 w-3.5 text-gold/70 shrink-0" />
                                <span>{c.phone}</span>
                            </div>
                        )}
                    </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gold shrink-0 mt-1" />
            </div>
        </Link>
    );
}

function NewCustomerModal({ onClose, onCreated }) {
    const [f, setF] = useState({ firstName: "", lastName: "", email: "", phone: "", countryPhoneIso: "gb" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (v) => setF({ ...f, [k]: v });
    const submit = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await staffApi.createCustomer(f);
            toast.success("Customer created");
            onCreated();
        } catch (e) { toast.error(e.message || "Could not create customer"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" data-testid="new-customer-modal">
            <div className="w-full max-w-md rounded-3xl glass-strong p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl text-white">New customer</h2>
                    <button onClick={onClose} className="text-white/50 text-sm">✕</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <TextField label="First name" testId="new-cust-first" value={f.firstName} onChange={set("firstName")} />
                    <TextField label="Last name" testId="new-cust-last" value={f.lastName} onChange={set("lastName")} />
                </div>
                <TextField label="Email" type="email" testId="new-cust-email" value={f.email} onChange={set("email")} />
                <TextField label="Phone" type="tel" testId="new-cust-phone" value={f.phone} onChange={set("phone")} />
                <div className="flex gap-2 pt-2">
                    <button onClick={submit} disabled={busy || !f.firstName || !f.lastName || !f.email || !f.phone} data-testid="new-cust-submit" className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">Create</button>
                    <button onClick={onClose} className="h-11 rounded-full border hairline text-white/60 px-5 text-sm">Cancel</button>
                </div>
            </div>
        </div>
    );
}

function TextField({ label, testId, value, onChange, type = "text" }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">{label}</div>
            <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
        </label>
    );
}
